import { React, useContext, useEffect, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Info } from "@mui/icons-material";
import CircleIcon from "@mui/icons-material/Circle";
import SearchIcon from "@mui/icons-material/Search";
import { Alert, AlertTitle, Button, Container, Divider, Stack, Switch } from "@mui/material";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { createTheme, styled, ThemeProvider } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";
import { useReactivateUserMutation } from "state/rtkQuery/user/userMutations";
import {
  useGetActiveUserStatusQuery,
  useGetUserQuery,
  useGetUserWithOrgMembershipStatusesQuery,
} from "state/rtkQuery/user/userQueries";
import { useGetAnnouncementsQuery } from "state/rtkQuery/announcements/announcementQueries";
import { useGetUserAccessQuery } from "state/rtkQuery/access/accessQueries";
import { useCurrentUserRoleNames, usePermission } from "components/RequirePermission/useAccessPermissions";
import { isCrossOrgAdminRole } from "components/RequirePermission/RequireEcrOpsRole";
import { setAuthState } from "state/slices/auth/authSlice";
import { setSelectedPlatform } from "state/slices/platformSlice";
import { setShowAllOrgsToggle } from "state/slices/showAllOrgsSlice";
import { alertSx, renderNoticeMessage } from "utils/announcements/announcementDisplay";
import { SIGNUPS_PAUSED_DISMISSED_KEY, SIGNUPS_TEMPORARILY_DISABLED } from "utils/constants";
import { showToast } from "utils/reusableToast";

import LicenseLimitModal from "./components/Modals/LicenseLimitationModal/LicenseLimitationModal";
import PlatformSelectionModal from "./components/Modals/PlatformSelectionModal/PlatformSelectionModal";
import ReactivationModal from "./components/Modals/ReactivationModal/ReactivationModal";
import SignupsPausedModal from "./components/Modals/SignupsPausedModal/SignupsPausedModal";
import OrgsDataGrid from "./components/OrgsDataGrid/OrgsDataGrid";
import InitiatedProgress from "./components/Table Progress Bars/InitiatedProgress";
import UserActions from "./components/UserActions/UserActions";

import PlatformSelector from "../../components/PlatformSelector/PlatformSelector";

import { useGetPlatformsQuery } from "../../state/rtkQuery/platform/platformQueries";
import { getMSALToken } from "../../utils/getMSALToken";

import "./HomeDashboard.css";
import TOSModal from "./components/Modals/TOSModal/TOSModal";


const HomeDashboard = () => {
  // Redux
  const authState = useSelector((state) => state.auth);
  const location = useLocation();

  // Fetched ONCE here rather than inside UserActions itself: UserActions is
  // rendered via the DataGrid's renderCell, which MUI can invoke repeatedly
  // per render pass (scroll, sort, refresh) — each invocation would otherwise
  // mount a fresh useEffectiveOrgRole subscription and re-fire the request.
  const { data: userOrganizationAccessData } = useGetUserAccessQuery(
    { email: authState?.userAccount?.username },
    { skip: !authState?.userAccount?.username }
  );
  const hasGlobalViewAccess = usePermission("organization:read");
  const currentUserRoleNames = useCurrentUserRoleNames();
  const isUserCrossOrgAdmin = isCrossOrgAdminRole(currentUserRoleNames) && hasGlobalViewAccess;
  // orgId -> whether the signed-in user can view that org's pending requests:
  // real Owner/Approver/Leadership/OperationsUser for that specific org, OR
  // one of the deliberately cross-org roles (EnterpriseAdmin/OperationsAdmin).
  // Leadership and OperationsUser are both intentionally org-scoped, not
  // part of the cross-org override — see isCrossOrgAdminRole.
  const isOrgAdminByOrgId = (orgId) => {
    if (isUserCrossOrgAdmin) return true;
    const role = userOrganizationAccessData?.organization?.find(
      (org) => org.org_id === orgId
    )?.role;
    return (
      role === "OrganizationOwner" ||
      role === "OrganizationApprover" ||
      role === "Leadership" ||
      role === "OperationsUser"
    );
  };
  const isOrgOwnerByOrgId = (orgId) =>
    userOrganizationAccessData?.organization?.find((org) => org.org_id === orgId)?.role ===
    "OrganizationOwner";
  const showAllOrgs = useSelector((state) => state.showAllOrgs.showAllOrgs);
  const dispatch = useDispatch();
  const isLicenseLimited = process.env.REACT_APP_GITHUB_LICENSE_LIMITED === "true";
  const [showLicenseModal, setShowLicenseModal] = useState(false);

  // Contexts
  const { instance, accounts } = useMsal();
  const handleReactivationCancel = () => {
    setShowReactivationModal(false);
    setShowTable(true);
    localStorage.clear();
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  };
  // Local States
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [query, setQuery] = useState("");
  const [queryData, setQueryData] = useState([]);
  const [authToken, setAuthToken] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isShowMoreOrgsLoading, setIsShowMoreOrgsLoading] = useState(false);
  const [showReactivationModal, setShowReactivationModal] = useState(false);
  const [signupsPausedDismissed, setSignupsPausedDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SIGNUPS_PAUSED_DISMISSED_KEY) === "true";
    } catch (e) {
      return false;
    }
  });
  const [isUserActive, setIsUserActive] = useState(true);
  const [showPlatformSelectionModal, setShowPlatformSelectionModal] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [shouldCheckPlatformSelection, setShouldCheckPlatformSelection] = useState(false);

  const [isReactivationCheckInProgress, setIsReactivationCheckInProgress] =
    useState(false);
  //Get the query from URL through searchParams and set it in the state
  useEffect(() => {
    const queryParam = searchParams.get("query");
    if (queryParam) {
      setQuery(queryParam);
      dispatch(setShowAllOrgsToggle(true));
    }
  }, [location]);

  // Handle search change
  const handleSearchChange = (event) => {
    setQuery(event.target.value);
    setSearchParams({ query: event.target.value });
  };

  // Fetch MSAL token
  useEffect(() => {
    getMSALToken(instance, accounts[0], setAuthToken);
  }, []);

  //Clear search query if query is empty in URL
  useEffect(() => {
    const queryParam = searchParams.get("query");
    if (queryParam === "" || queryParam === null) {
      setQuery("");
    }
  }, [searchParams]);

  //Show toast message when user signs in to home dashboard
  useEffect(() => {
    if (
      authState?.userAccount &&
      !isPageLoading &&
      !sessionStorage.getItem("welcomeToastShown")
    ) {
      showToast({
        title: `Welcome, ${authState.userAccount.givenName}!`,
        description: "You have successfully signed in.",
        type: "success",
        duration: 5000,
      });
      sessionStorage.setItem("welcomeToastShown", "true");
    }
  }, [isPageLoading]);

  // RTK Query for getting platform data
  const {
    data: platformData,
    isLoading: isPlatformDataLoading,
    isSuccess: isPlatformDataSuccess,
    error: isPlatformDataError,
    refetch: refetchPlatformData,
  } = useGetPlatformsQuery();

  // Get selected platform from Redux state (for multi-platform support)
  const selectedPlatformId = useSelector((state) => state.platform?.selectedPlatformId);
  const selectedPlatformName = useSelector((state) => state.platform?.platformName);
  // Use selected platform if available, otherwise fall back to platformData
  const activePlatformId = selectedPlatformId || platformData?._id;

  // RTK Query for getting user entry by email
  const {
    data: userData,
    status: userDataStatus,
    isLoading: isUserDataLoading,
    isSuccess: isUserDataSuccess,
    refetch: refetchUserData,
    error: userDataError,
  } = useGetUserQuery(
    { email: accounts?.[0]?.username, platformId: activePlatformId },
    { skip: !activePlatformId }
  );
  const email = userData?.[0]?.email;
  const platformId = activePlatformId;
  const userOrganizations = userData?.[0]?.platforms?.[platformId]?.organizations;
  const hasAnyOrganizationsOnPlatform =
    !!userOrganizations && Object.keys(userOrganizations).length > 0;

  // When selected platform changes, refresh the user entry and decide whether
  // to show the TOS modal or the organizations table for that platform.
  useEffect(() => {
    const handlePlatformChange = async () => {
      if (!activePlatformId) return;

      // Reset UI while loading
      setShowTable(false);
      setShowModal(false);
      setIsPageLoading(true);

      try {
        const res = await refetchUserData();
        const latest = res?.data || userData || [];
        const platformTos = latest?.[0]?.platforms?.[activePlatformId]?.tos_accepted;

        if (platformTos === true) {
          // TOS accepted: decide whether to show platform selection modal or table
          const userOrgs = latest[0]?.platforms?.[activePlatformId]?.organizations;
          const hasOrganizations = userOrgs && Object.keys(userOrgs).length > 0;

          if (!hasOrganizations) {
            // User has no organizations - check if they should see platform selection modal
            const userCountry = latest[0]?.country;
            const isUSUser = userCountry === "United States" || userCountry === "US" || userCountry === "USA";

            // Check if platform selection modal was already shown this session
            const platformSelectionKey = `platformSelectionShown:${accounts?.[0]?.username || latest[0]?.email || "guest"}`;
            let alreadyShown = false;
            try {
              alreadyShown = sessionStorage.getItem(platformSelectionKey) === "true";
            } catch (e) {}

            if (isUSUser && !alreadyShown) {
              // Show platform selection modal for US users with no organizations (once per session)
              setShowPlatformSelectionModal(true);
              setIsPageLoading(false);
              setIsNewUser(false);
            } else {
              // Non-US users or already shown modal - go directly to table
              try {
                await refetchUserOrgsData();
              } catch (e) {}
              setShowTable(true);
              setIsPageLoading(false);
              setIsNewUser(false);
            }
          } else {
            // User has organizations, fetch and show table
            try {
              await refetchUserOrgsData();
            } catch (e) {}
            setShowTable(true);
            setIsNewUser(false);
            setIsPageLoading(false);
          }
        } else {
          // TOS not accepted: show TOS modal
          setShowModal(true);
          setIsPageLoading(false);
          if (!latest || latest.length === 0) {
            setIsNewUser(true);
          } else {
            const userOrgs = latest[0]?.platforms?.[activePlatformId]?.organizations;
            const hasOrgs = userOrgs && Object.keys(userOrgs).length > 0;
            setIsNewUser(!hasOrgs);
          }
        }
      } catch (err) {
        setIsPageLoading(false);
      }
    };

    handlePlatformChange();
  }, [activePlatformId]);

  // Live announcement banners — replaces the three hardcoded notices. Dismiss
  // is session-only local state (no API call), per how the backend read
  // endpoint works: it always returns every enabled announcement, dismissal
  // is purely a client-side "hide until next load" concern. Polls every
  // minute so an admin publishing/editing an announcement shows up for users
  // already sitting on this page, without requiring a manual refresh.
  const { data: announcements = [] } = useGetAnnouncementsQuery(platformId, {
    skip: !platformId,
    pollingInterval: 60000,
  });
  const [dismissedAnnouncementIds, setDismissedAnnouncementIds] = useState(new Set());
  const visibleAnnouncements = announcements
    .filter((a) => !dismissedAnnouncementIds.has(a.id))
    .sort((a, b) => a.order - b.order);
  const dismissAnnouncement = (id) =>
    setDismissedAnnouncementIds((prev) => new Set(prev).add(id));
  // RTK Query for GET active user status
  const {
    data: activeUserStatus,
    isLoading: isActiveUserLoading,
    isSuccess: isActiveUserSuccess,
    refetch: refetchActiveUserStatus,
  } = useGetActiveUserStatusQuery({ email }, { skip: !email });

  // RTK Query for POST reactivation
  const [reactivateUser, { isLoading: isReactivating }] =
    useReactivateUserMutation();

  // Note: TOS checking is now handled entirely by the handlePlatformChange effect above.
  // This prevents race conditions where multiple effects compete to set showModal/showTable.

  // Check platform selection after userData refetch for new users
  useEffect(() => {
    if (shouldCheckPlatformSelection && userData && userData.length > 0) {

      const userCountry = userData[0]?.country;

      const isUSUser = userCountry === "United States" || userCountry === "US" || userCountry === "USA";

      // Reset the flag
      setShouldCheckPlatformSelection(false);

      // Check if platform selection modal was already shown this session
      const platformSelectionKey = `platformSelectionShown:${accounts?.[0]?.username || userData[0]?.email || "guest"}`;
      let alreadyShown = false;
      try {
        alreadyShown = sessionStorage.getItem(platformSelectionKey) === "true";
      } catch (e) {}

      // Only show platform selection modal for US-based new users who haven't seen it yet
      if (isUSUser && !alreadyShown) {
        setShowPlatformSelectionModal(true);
      } else {
        setShowTable(true);
      }
    }
  }, [shouldCheckPlatformSelection, userData]);

  // Use effect to show modal if inactive
  useEffect(() => {

    if (
      activeUserStatus &&
      activeUserStatus.is_active === false &&
      activeUserStatus.end_date
    ) {
      const endDate = dayjs(activeUserStatus.end_date, "MMM DD, YYYY");
      const today = dayjs();

      if (endDate.isSame(today, "day") || endDate.isAfter(today, "day")) {
        // Show reactivation modal
        setIsUserActive(false);
        setShowReactivationModal(true);
        setIsPageLoading(false);
        setShowTable(false);
        setIsReactivationCheckInProgress(false);
      } else {
        // Account already deactivated, show page as normal (no modal)
        setIsUserActive(false);
        setShowTable(true);
        setIsPageLoading(false);
        setIsReactivationCheckInProgress(false);
        setShowReactivationModal(false);
      }
    } else if (
      activeUserStatus &&
      activeUserStatus.is_active === true &&
      userData &&
      userData.length !== 0 &&
      platformId &&
      userData[0]?.platforms?.[platformId]?.tos_accepted === true
    ) {
      setIsUserActive(true);
      setShowTable(true);
      setIsPageLoading(false);
      setIsReactivationCheckInProgress(false);
    }
  }, [activeUserStatus, userData, platformId]);

  // Reactivate handler
  const handleReactivate = async () => {
    try {
      await reactivateUser({
        updated_by: "arbiter",
        users: [
          {
            email,
            business_id: platformData?.ref_id,
          },
        ],
      }).unwrap();
      setShowReactivationModal(false);
      setIsPageLoading(true);
      setShowTable(false);
      // Refetch all APIs
      refetchActiveUserStatus();
      refetchUserOrgsData();
      refetchPlatformData();
      refetchUserData();

      showToast({
        title: "Account Reactivated Successfully",
        description: `Your account has been reactivated successfully!`,
        type: "success",
        duration: 8000,
      });
      // Optionally, refetch platforms and userData if needed
    } catch (err) {
      setShowReactivationModal(false);
      setShowTable(true);
    }
  };
  // RTK Query for getting user with org membership statuses if user data and platform data has been fetched
  const {
    data: userOrgsData,
    isLoading: isUserOrgsDataLoading,
    isSuccess: isUserOrgsDataSuccess,
    refetch: refetchUserOrgsData,
  } = useGetUserWithOrgMembershipStatusesQuery(
    { userId: userData?.[0]?._id, platformId: activePlatformId },
    {
      skip:
        !isUserDataSuccess || !activePlatformId || userData?.length === 0,
      pollingInterval: 300000,
    }
  );

  // Set rows and queryData once userOrgsData is fetched
  useEffect(() => {
    if (isUserOrgsDataSuccess) {
      let orgsToShow = [...userOrgsData[0]];
      if (showAllOrgs) {
        orgsToShow = [...orgsToShow, ...userOrgsData[1]];
      }

      setRows(orgsToShow);
      setQueryData(orgsToShow);

      setIsInitialRender(false);
      setTimeout(() => {
        setIsShowMoreOrgsLoading(false);
      }, [1000]);
    }
  }, [isUserOrgsDataSuccess, userOrgsData, showAllOrgs]);

  // Set columns for MUI DataGrid once userOrgsData is fetched
  useEffect(() => {
    if (authToken) {
      setColumns([
        // Org Name
        {
          field: "organization",
          headerName: "Organization",
          headerClassName: "orgName-column-header",
          headerAlign: "left",
          align: "left",
          flex: 1.3,
          minWidth: 200,
          renderCell: (params) => (
            <Tooltip
              title={
                <p
                  style={{
                    fontSize: "13px",
                    width: "230px",
                    height: "fitContent",
                  }}
                >
                  {params.row.recommended
                    ? `The ${params.row.orgDisplayName} org
                  is recommended for you to join.`
                    : ` ${params.row.orgDisplayName}`}
                </p>
              }
              placement="left"
            >
              <span className="table-cell-home-OrgDisplayName">
                <p className="table-cell-orgName ">
                  <strong>{params.row.orgDisplayName}</strong>
                </p>

                {params.row.recommended ? (
                  <span>
                    <i>Recommended</i>
                  </span>
                ) : (
                  ""
                )}
              </span>
            </Tooltip>
          ),
        },
        // Approval Required
        {
          field: "approvalRequired",
          headerName: "Approval Required",
          description: "Requires Approval",
          flex: 0.8,
          minWidth: 130,
          headerAlign: "center",
          align: "center",
          filterable: false,
          renderCell: (params) => (
            <Tooltip
              title={
                params.row.approvalRequired === "Yes"
                  ? "Approval Required"
                  : "Approval Not Required"
              }
            >
              <span className="approval-column-val">
                <CircleIcon
                  style={{
                    color:
                      params.row.approvalRequired === "Yes"
                        ? "#FF0000"
                        : "green",
                  }}
                />
              </span>
            </Tooltip>
          ),
        },
        // Request Status
        {
          field: "status",
          headerName: "Request Status",
          flex: 1.9,
          minWidth: 300,
          align: "center",
          headerAlign: "center",
          filterable: true,
          renderCell: (params) => <InitiatedProgress {...{ params }} />,
        },
        // Action Buttons
        {
          field: "action",
          headerAlign: "center",
          headerName: "Actions",
          align: "center",
          flex: 1.75,
          minWidth: 320,
          filterable: false,
          renderCell: (params) => (
            <UserActions
              params={params}
              refetchOrgStatus={refetchUserOrgsData}
              hasJoinedOrg={hasJoinedOrg}
              hasAnyOrganizationsOnPlatform={hasAnyOrganizationsOnPlatform}
              isOrgOwner={isOrgOwnerByOrgId(params?.row?.id)}
              isOrgAdmin={isOrgAdminByOrgId(params?.row?.id)}
            />
          ),
        },
      ]);
    }
  }, [authToken, userData, userOrganizationAccessData, isUserCrossOrgAdmin]);

  // Set queryData based on row data and initial query (this data will be shown in the table)
  useEffect(() => {
    setQueryData(
      rows.filter((post) => {
        if (query === "") {
          return rows;
        } else if (
          post.orgDisplayName.toLowerCase().includes(query.toLowerCase())
        ) {
          return post;
        }
      })
    );

    // Set current page to last available page if current page is greater than total pages
    if (currentPage > Math.ceil(queryData.length / 10)) {
      setCurrentPage(Math.max(1, Math.ceil(queryData.length / 10)));
    }
  }, [rows]);

  // Handles subsequent search query changes
  useEffect(() => {
    setCurrentPage(1);
    setQueryData(
      rows.filter((post) => {
        if (query === "") {
          return rows;
        } else if (
          post.orgDisplayName.toLowerCase().includes(query.toLowerCase())
        ) {
          return post;
        }
      })
    );
  }, [query]);

  // Styles for MUI TextField
  const textStyles = styled({
    root: {
      "& .Mui-focused": {
        color: "#008080",
        outline: "1px solid  #008080",
      },
    },
  });

  // Handles closing TOS modal
  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
    } else {
      setShowModal(false);
      setShowTable(true);
    }
  };

  // Handles the user backing out of the TOS modal without accepting.
  // Reverts the platform selection back to the default (deloitte-global) so
  // the dashboard doesn't stay pointed at a platform the user hasn't
  // accepted TOS for, then closes the modal.
  const handleCancelTos = () => {
    dispatch(setSelectedPlatform({ platformId: null, platformName: null }));
    try {
      localStorage.removeItem("selectedPlatform");
    } catch (e) {
      // ignore storage errors
    }
    setShowModal(false);
    setShowTable(true);
  };

  // Handle TOS acceptance for new users
  const handleTosAcceptedNewUser = async () => {
    // Set flag to check platform selection after userData is refetched
    setShouldCheckPlatformSelection(true);
    // Refetch user data to get the newly created user with country info
    await refetchUserData();
  };

  // Handle continue to ECR platform
  const handleContinueToECR = () => {
    // mark that we've shown/handled the platform selection for this session/user
    try {
      const key = `platformSelectionShown:${accounts?.[0]?.username || email || "guest"}`;
      sessionStorage.setItem(key, "true");
    } catch (e) {
      // ignore
    }
    setShowPlatformSelectionModal(false);
    setShowTable(true);
  };

  // Handle redirect to Nexus
  const handleRedirectToNexus = () => {
    // Redirect to Nexus platform URL
    try {
      const key = `platformSelectionShown:${accounts?.[0]?.username || email || "guest"}`;
      sessionStorage.setItem(key, "true");
    } catch (e) {}
    const nexusUrl = process.env.REACT_APP_NEXUS_PLATFORM_URL || "https://nexus.platform.url";
    window.location.href = nexusUrl;
  };

  // Handle redirect to AEF (pass to modal so clicks also mark the flag)
  const handleRedirectToAEF = () => {
    try {
      const key = `platformSelectionShown:${accounts?.[0]?.username || email || "guest"}`;
      sessionStorage.setItem(key, "true");
    } catch (e) {}
    const aefUrl = process.env.REACT_APP_AEF_PLATFORM_URL || "https://engineeringplatforms.deloitte.com";
    window.location.href = aefUrl;
  };

  // Show all organizations on the table
  const showAllOrganizations = () => {
    setIsShowMoreOrgsLoading(true);
    dispatch(setShowAllOrgsToggle(!showAllOrgs));
  };

  // Theme for tooltip
  const welcomeToolTipTheme = createTheme({
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: "0.9rem", // Adjust this value to your needs
            padding: "8px 12px",
          },
        },
      },
    },
  });
  useEffect(() => {
    document.title = "ECR Signup Portal";
  }, []);

  const theme = createTheme({});
  const hasJoinedOrg = Array.isArray(rows) && rows.some(org => org.status === "Joined");

  // Show license modal only if license is limited and user is NOT joined in any org
  useEffect(() => {
    if (isUserOrgsDataSuccess && Array.isArray(rows) && rows.length > 0 && isLicenseLimited && showTable && !hasJoinedOrg) {
      setShowLicenseModal(true);
    } else {
      setShowLicenseModal(false);
    }
  }, [isUserOrgsDataSuccess, rows, isLicenseLimited, showTable, hasJoinedOrg]);




  return (
    <Container sx={{ paddingTop: "0rem" }}>
      {/* Loading Spinner for initial render */}
      {(isInitialRender && isPageLoading && isUserDataLoading) ||
        isReactivationCheckInProgress ? (
        <Box
          sx={{
            height: 420,
            marginTop: "4rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "auto",
          }}
        >
          <TailSpin
            color="hsl(81.43deg 69.37% 43.53%)"
            height={50}
            width={50}
          />
        </Box>
      ) : (
        <div>
          {/* TOS Modal */}
          {showModal ? (
            <TOSModal
              show={showModal}
              close={handleClose}
              email={authState.userAccount?.username}
              firstName={authState.userAccount?.givenName}
              lastName={authState.userAccount?.surname}
              platformId={activePlatformId}
              platformName={selectedPlatformName || platformData?.name}
              userData={userData}
              onTosAcceptedNewUser={isNewUser ? handleTosAcceptedNewUser : undefined}
              onCancel={handleCancelTos}
              refetchUserData={refetchUserData}
              refetchUserOrgsData={refetchUserOrgsData}
            />
          ) : showTable ? (
            <>
              <Box
                sx={{
                  height: 740,
                  marginTop: "2.5rem",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* ── Announcements ───────────────────────────────────────── */}
                {visibleAnnouncements.length > 0 && (
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {visibleAnnouncements.map((announcement) => (
                      <Alert
                        key={announcement.id}
                        severity={announcement.severity}
                        onClose={announcement.dismissible ? () => dismissAnnouncement(announcement.id) : undefined}
                        sx={alertSx(announcement.severity)}
                      >
                        {announcement.title ? (
                          <AlertTitle sx={{ fontSize: "0.85rem", fontWeight: 700 }}>{announcement.title}</AlertTitle>
                        ) : null}
                        {renderNoticeMessage(announcement.message)}
                      </Alert>
                    ))}
                  </Stack>
                )}
                {/* Header */}
                <div className="headerSearchFlex">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                  >
                    <span style={{ fontSize: "24px", fontWeight: "800" }}>
                      GitHub Global Organizations
                    </span>
                  </div>
                </div>
                <Divider />
                <div
                  className="welcome-message"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "left",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "14px",
                    }}
                  >
                    <span>
                      Organizations marked as {<i>"Recommended"</i>} are
                      suggested for you to join based on the information in your
                      Active Directory profile
                    </span>
                    <ThemeProvider theme={welcomeToolTipTheme}>
                      <Tooltip
                        placement="right"
                        title={
                          <div>
                            {
                              "Recommendations are currently based on department, business function and country. This may change as new filters continue to be developed"
                            }
                          </div>
                        }
                        sx={{
                          marginLeft: "5px",
                          color: "#008080",
                          cursor: "pointer",
                        }}
                      >
                        <Info />
                      </Tooltip>
                    </ThemeProvider>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "15px",
                      justifyContent: "center",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}
                  >
                    {/* Platform Selector */}
                    <PlatformSelector />
                    
                    {/* Search TextField */}
                    <ThemeProvider theme={theme}>
                      <TextField
                        id="outlined-search"
                        placeholder="Search organizations"
                        type="search"
                        className={textStyles.root}
                        variant="outlined"
                        InputProps={{
                          style: {
                            height: "31px",
                            minWidth: "300px",
                          },
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        onChange={(event) => handleSearchChange(event)}
                        value={query}
                      />
                    </ThemeProvider>
                  </div>
                  <div>
                    <span
                      style={{
                        color: showAllOrgs ? "black" : "black",
                        fontWeight: showAllOrgs ? "bold" : "normal",
                      }}
                    >
                      {"Show All Organizations"}
                    </span>
                    <ThemeProvider theme={welcomeToolTipTheme}>
                      <Tooltip
                        title={
                          showAllOrgs
                            ? "Currently showing all orgs. Toggle to show recommended orgs only."
                            : "Currently showing joined and recommended orgs only. Toggle to show all orgs."
                        }
                        arrow
                      >
                        <Switch
                          checked={showAllOrgs}
                          aria-label="ShowAllOrgs"
                          onClick={() => showAllOrganizations()}
                          sx={{
                            "& .Mui-checked": {
                              color: "hsl(81.43deg 69.37% 43.53%) !important",
                            },
                            "& .Mui-checked + .MuiSwitch-track": {
                              backgroundColor:
                                "hsl(81.43deg 69.37% 43.53%) !important",
                            },
                          }}
                        />
                      </Tooltip>
                    </ThemeProvider>
                  </div>
                </div>
                {/* DataGrid */}
                <OrgsDataGrid
                  totalRecords={userOrgsData?.length}
                  columns={columns}
                  isTableLoading={isUserOrgsDataLoading}
                  isInitialRender={isInitialRender}
                  isShowMoreOrgsLoading={isShowMoreOrgsLoading}
                  rows={rows}
                  queryData={queryData}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                />
                {/* Footer/Support Request Form */}
              </Box>
              <div style={{ marginTop: "30px", padding: "10px 0px" }}>
                <p>
                  Please fill our{" "}
                  <a
                    href="https://deloitteglobal.service-now.com/sp?id=sc_cat_item&sys_id=4a417b4f1b17855cbcae55b0604bcb1c"
                    target="_blank"
                    style={{ color: "rgb(0 151 215)", fontWeight: "500" }}
                    rel="noreferrer"
                  >
                    Support Request
                  </a>
                  &nbsp; Form if you face any issues.
                </p>
              </div>
            </>
          ) : (
            <></>
          )}
        </div>
      )}
      <ReactivationModal
        show={showReactivationModal}
        close={() => {
          setShowReactivationModal(false);
          localStorage.clear();
          instance.logoutRedirect({
            postLogoutRedirectUri: "/",
          });
        }}
        handleReactivate={handleReactivate}
        firstName={userData?.[0]?.first_name}
        lastName={userData?.[0]?.last_name}
        endDate={activeUserStatus?.end_date}
        isLoading={isReactivating}
      />
      {/* Lowest precedence: only after the TOS, reactivation and platform
          selection flows are done and the page itself is showing */}
      <SignupsPausedModal
        show={
          SIGNUPS_TEMPORARILY_DISABLED &&
          !signupsPausedDismissed &&
          showTable &&
          !showModal &&
          !showReactivationModal &&
          !showPlatformSelectionModal &&
          !isPageLoading &&
          !isActiveUserLoading &&
          !isReactivationCheckInProgress
        }
        close={() => {
          setSignupsPausedDismissed(true);
          try {
            sessionStorage.setItem(SIGNUPS_PAUSED_DISMISSED_KEY, "true");
          } catch (e) {}
        }}
      />
      <LicenseLimitModal show={showLicenseModal} onClose={() => setShowLicenseModal(false)} />
      <PlatformSelectionModal
        show={showPlatformSelectionModal}
        onContinue={handleContinueToECR}
        onRedirectToNexus={handleRedirectToNexus}
      />
    </Container>
  );
};

export default HomeDashboard;
